import { useCallback, useEffect, useRef } from 'react';
import { useIsMounted } from '~/hooks';

const KEEPALIVE_INTERVAL = 5_000; // Must match backend keepalive interval
const SLOW_THRESHOLD = 4; // 4 missed keepalives (20s) = show warning
const DEAD_THRESHOLD = 10; // 10 missed keepalives (50s) = show refresh button

async function streamResponse({
  url,
  payload,
  signal,
  onDelta,
  onChart,
  onTable,
  onSchedule,
  onPivot,
  onComponent,
  onStatusText,
  onItemPending,
  onToolDetected,
  onToolComplete,
  onStatusUpdate,
  onSignal,
  onComplete,
  onError,
  onConnectionSlow,
  onConnectionRecovered,
  onConnectionDead,
  onConversationCreated,
}) {
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!res.ok || !res.body) {
    let message = 'Something went wrong. Please try again.';
    let code;
    try {
      const body = await res.json();
      if (body.message) message = body.message;
      code = body.code;
    } catch {
      // response may not be JSON
    }
    const error = new Error(message);
    error.code = code;
    throw error;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();

  let buffer = '';
  let missedKeepalives = 0;
  let connectionState = 'ok'; // 'ok' | 'slow' | 'dead'

  const keepaliveChecker = setInterval(() => {
    missedKeepalives++;
    if (missedKeepalives >= DEAD_THRESHOLD && connectionState !== 'dead') {
      connectionState = 'dead';
      onConnectionDead?.();
    } else if (missedKeepalives >= SLOW_THRESHOLD && connectionState === 'ok') {
      connectionState = 'slow';
      onConnectionSlow?.();
    }
  }, KEEPALIVE_INTERVAL);

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      // Data arrived — reset keepalive counter
      if (missedKeepalives > 0) {
        const wasDisconnected = connectionState !== 'ok';
        missedKeepalives = 0;
        connectionState = 'ok';
        if (wasDisconnected) onConnectionRecovered?.();
      }

      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split('\n\n');
      buffer = parts.pop() || '';

      for (const chunk of parts) {
        const lines = chunk.split('\n');

        let eventName = 'message';
        let dataLine = null;

        for (const line of lines) {
          if (line.startsWith('event:')) eventName = line.replace(/^event:\s*/, '').trim();
          if (line.startsWith('data:')) dataLine = line.replace(/^data:\s*/, '').trim();
        }

        if (!dataLine) continue;

        const data = JSON.parse(dataLine);

        switch (eventName) {
          case 'text':
            onDelta?.(data.delta);
            break;

          case 'chart':
            onChart?.(data);
            break;

          case 'statusText':
            onStatusText?.(data.statusText);
            break;

          case 'itemPending':
            onItemPending?.(data);
            break;

          case 'toolDetected':
            onToolDetected?.(data);
            break;

          case 'toolComplete':
            onToolComplete?.(data);
            break;

          case 'table':
            onTable?.(data);
            break;

          case 'schedule':
            onSchedule?.(data);
            break;

          case 'pivot':
            onPivot?.(data);
            break;

          case 'component':
            onComponent?.(data);
            break;

          case 'statusUpdate':
            onStatusUpdate?.(data);
            break;

          // Server→client side-effect event ({ key, ...params }) from a tool's effects — dispatched by key,
          // never appended to the message. Live-only; not replayed on reload.
          case 'signal':
            onSignal?.(data);
            break;

          case 'error':
            onError?.(data.message);
            break;

          case 'conversationCreated':
            onConversationCreated?.(data.id);
            break;
        }
      }
    }
  } catch {
    // Mid-stream network error — show the dead warning so user can refresh.
    if (connectionState !== 'dead') {
      connectionState = 'dead';
      onConnectionDead?.();
    }
  } finally {
    clearInterval(keepaliveChecker);
  }

  if (!signal.aborted) {
    await onComplete?.();
  }
}

// Drops the placeholder held for a render item whose JSON is still arriving.
function dropPending(parts) {
  return parts.filter((part) => part.type !== 'pending');
}

export default function useStream({ setMessages, setThinking, setThinkingText }) {
  const isMounted = useIsMounted();
  const abortControllerRef = useRef(null);

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const run = useCallback(
    async (
      url,
      payload,
      {
        afterComplete,
        onConnectionSlow,
        onConnectionRecovered,
        onConnectionDead,
        onConversationCreated,
        onSignal,
      } = {},
    ) => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      setThinking(true);
      setThinkingText?.(null);

      // Same-value updates bail out in React, so the per-delta call below costs nothing once cleared.
      const clearThinkingText = () => setThinkingText?.((current) => (current ? null : current));

      await streamResponse({
        url,
        payload,
        signal,

        onStatusText: (statusText) => {
          if (!isMounted.current) return;
          setThinking(true);
          setThinkingText?.(statusText || null);
        },

        onItemPending: ({ itemType }) => {
          if (!isMounted.current) return;
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];
            // One at a time: a second opening means the first already closed or will never arrive.
            last.parts = [...dropPending(last.parts), { type: 'pending', itemType }];
            return updated;
          });
        },

        onToolDetected: ({ callId, parentCallId, toolName, statusText }) => {
          if (!isMounted.current) return;
          // Reasoning is over once there is a step to show.
          clearThinkingText();
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];
            let toolsPart = last.parts.find((p) => p.type === 'tools');
            if (!toolsPart) {
              toolsPart = { type: 'tools', tools: [] };
              last.parts.unshift(toolsPart);
            }
            if (!toolsPart.tools.some((t) => t.callId === callId)) {
              toolsPart.tools = [...toolsPart.tools, { callId, parentCallId, toolName, statusText, status: 'running' }];
            }
            return updated;
          });
        },

        onToolComplete: ({ callId, success }) => {
          if (!isMounted.current) return;
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];
            const toolsPart = last.parts.find((p) => p.type === 'tools');
            if (toolsPart) {
              toolsPart.tools = toolsPart.tools.map((t) =>
                t.callId === callId ? { ...t, status: success ? 'completed' : 'failed' } : t,
              );
            }
            return updated;
          });
        },

        onStatusUpdate: ({ callId, statusText }) => {
          if (!isMounted.current) return;
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];
            const toolsPart = last.parts.find((p) => p.type === 'tools');
            if (toolsPart) {
              toolsPart.tools = toolsPart.tools.map((t) => (t.callId === callId ? { ...t, statusText } : t));
            }
            return updated;
          });
        },

        onSignal: (sig) => {
          if (!isMounted.current) return;
          onSignal?.(sig);
        },

        onError: (message) => {
          if (!isMounted.current) return;
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];
            last.parts.forEach((p) => delete p._streaming);
            if (!last.parts.some((p) => p.type === 'error')) {
              last.parts.push({ type: 'error', message });
            }
            last.parts = dropPending(last.parts);
            return updated;
          });
          setThinking(false);
          setThinkingText?.(null);
        },

        onConnectionSlow: () => {
          if (!isMounted.current) return;
          onConnectionSlow?.();
        },

        onConnectionRecovered: () => {
          if (!isMounted.current) return;
          onConnectionRecovered?.();
        },

        onConnectionDead: () => {
          if (!isMounted.current) return;
          onConnectionDead?.();
        },

        onConversationCreated: (id) => {
          if (!isMounted.current) return;
          onConversationCreated?.(id);
        },

        onDelta: (delta) => {
          clearThinkingText();
          if (!isMounted.current) return;
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];

            let textPart = last.parts.find((p) => p.type === 'text' && p._streaming);
            if (!textPart) {
              textPart = { type: 'text', content: '', _streaming: true };
              last.parts.push(textPart);
            }
            textPart.content += delta;

            return updated;
          });
        },

        onChart: (chartData) => {
          if (!isMounted.current) return;
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];

            const currentText = last.parts.find((p) => p._streaming);
            if (currentText) delete currentText._streaming;

            last.parts = dropPending(last.parts);

            last.parts.push({ type: 'chart', ...chartData });
            return updated;
          });
        },

        onTable: ({ data, fields, display, meta, pagination, filename }) => {
          if (!isMounted.current) return;
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];

            const currentText = last.parts.find((p) => p._streaming);
            if (currentText) delete currentText._streaming;

            last.parts = dropPending(last.parts);

            last.parts.push({ type: 'table', data, fields, display, meta, pagination, filename });
            return updated;
          });
        },

        onSchedule: ({ data, fields, meta, pagination, filename }) => {
          if (!isMounted.current) return;
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];

            const currentText = last.parts.find((p) => p._streaming);
            if (currentText) delete currentText._streaming;

            last.parts = dropPending(last.parts);

            last.parts.push({ type: 'schedule', data, fields, meta, pagination, filename });
            return updated;
          });
        },

        onPivot: ({ data, fields, meta, pagination, filename, cellValue }) => {
          if (!isMounted.current) return;
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];

            const currentText = last.parts.find((p) => p._streaming);
            if (currentText) delete currentText._streaming;

            last.parts = dropPending(last.parts);

            last.parts.push({ type: 'pivot', data, fields, meta, pagination, filename, cellValue });
            return updated;
          });
        },

        onComponent: ({ callId, data, fields, meta, pagination, filename }) => {
          if (!isMounted.current) return;
          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];

            const currentText = last.parts.find((p) => p._streaming);
            if (currentText) delete currentText._streaming;

            last.parts = dropPending(last.parts);

            last.parts.push({ type: 'component', callId, data, fields, meta, pagination, filename });
            return updated;
          });
        },

        onComplete: async () => {
          if (!isMounted.current) return;

          setMessages((msgs) => {
            if (!msgs) return msgs;
            const updated = [...msgs];
            const last = updated[updated.length - 1];
            last.parts.forEach((p) => delete p._streaming);
            last.parts = dropPending(last.parts);
            return updated;
          });

          setThinking(false);
          setThinkingText?.(null);
          await afterComplete?.();
        },
      });
    },
    [isMounted, setMessages, setThinking, setThinkingText],
  );

  return { run, abort };
}
